import {
  DataBlockDictionaryDto,
  DataBlockVariant,
  QueryFactory,
} from '@lmt/shared-types';
import {
  CreateDataBlockFieldDto,
  DataBlockTypeDto,
  DataType,
  DataBlockFieldListDto,
} from '@lmt/shared-types';

import { AutoSizedTableWrapper } from '@lmt/unicorn-shared/app/components/common/AutoSizedTableWrapper';

import { ButtonColor } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { parseError } from '@lmt/unicorn-shared/app/helpers/error-handling';
import { mapExcelRowToDataGrid } from '@lmt/unicorn-shared/app/helpers/excel-to-grid-helper';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  getFieldLocalizationKey,
  onlyActiveDictionaries,
  useDataTypesList,
} from '../common';
import {
  NS_DataBlockFields,
  useScopedTranslation,
} from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { sharedTypeNames } from '../../measurements/common';
import {
  useDataGrid,
  DXG,
  DataGrid,
} from '@lmt/unicorn-shared/app/common/devexpress/Grid/dx-grid';
import { createId } from '@lmt/unicorn-shared/app/helpers/create-id';
import { useGlobalForms } from '../../global-forms/useGlobalForms';
import { useDataGridExportHandler } from '@lmt/unicorn-shared/app/helpers/devextreme-helper';
import { useEventCallback } from '@mui/material';
import { allowOnlySingleNewRow } from '@lmt/unicorn-shared/app/common/devexpress/Grid/grid-helper';
import { readFileAsExcel } from '@lmt/unicorn-shared/app/helpers/file-helper';
import { ValidationProblemDetails } from '@lmt/shared-types';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';

interface TableProps {
  dataBlockType: DataBlockTypeDto;
  dataBlockFields: DataBlockFieldListDto[];
}

type Row = {
  row: RowData;
};
type RowData = {
  data: DataBlockFieldListDto;
  isNewRow: boolean;
  removed: boolean;
  key: number;
};

export const DataBlockFieldsTable: FC<TableProps> = (props) => {
  const { dataBlockType, dataBlockFields } = props;
  useDataGrid();
  const i18n = useTranslation();
  const i18nDictionaries = useTranslation(NS_DataBlockFields);
  const i18nFields = useScopedTranslation('data_blocks.fields');
  const i18nConvertCustomValueToItem = useScopedTranslation(
    'data_blocks.fields.create_dictionary_items_from_custom_values',
  );
  const queryClient = useQueryClient();
  const { data: dictionaries } =
    QueryFactory.DataBlockDictionaryQuery.useGetQuery({
      select: onlyActiveDictionaries,
    });
  const modals = useModal();
  const forms = useGlobalForms();
  const claims = useClaims();

  const fieldQuery = QueryFactory.DataBlockFieldQuery.useGetQuery(
    dataBlockType.id,
  );

  const sortedDictionaries = useMemo(() => {
    return dictionaries
      ?.map((d) => {
        return {
          ...d,
          translatedTitle: i18nDictionaries.t(d.title as any, {
            defaultValue: d.title,
          }),
        };
      })
      ?.sort((a, b) =>
        i18nDictionaries
          .t(a.title as any)
          .localeCompare(i18nDictionaries.t(b.title as any)),
      );
  }, [dictionaries]);

  const updateFields = useEventCallback(async () => {
    const state = dataGridRef.current?.instance().state();

    await queryClient.invalidateQueries({
      queryKey:
        QueryFactory.DataBlockFieldQuery.getDataBlockFieldsForEditingQueryKey(
          dataBlockType.id,
        ),
    });
    await queryClient.invalidateQueries({
      queryKey: QueryFactory.DataBlockFieldQuery.getQueryKey(dataBlockType.id),
    });
    dataGridRef.current?.instance().state(state);
  });

  // When creating or renaming a data block field
  // and getting ValidationError with only an incorrect name.
  // We need to show only error. In other cases we need to keep fields names to detect issues
  const parseValidationError = (error: ValidationProblemDetails): string => {
    if (
      error.errors &&
      Object.keys(error.errors).length === 1 &&
      error.errors?.['Name']
    ) {
      return error.errors['Name'].join(',');
    }

    return parseError(error);
  };

  // async function onRowUpdate(
  //   oldData: DataBlockFieldDto,
  //   newData: DataBlockFieldDto,
  // ) {
  //   const dto: UpdateDataBlockFieldDto = {
  //     name: newData.name || oldData.name,
  //     dataType: newData.dataType || oldData.dataType,
  //   };
  //   try {
  //     await QueryFactory.DataBlockFieldClient.edit(
  //       oldData.id,
  //       dto,
  //     );
  //   } catch (error) {
  //     await modals.showError({ text: parseError(error) });
  //   }
  //   await updateFields();
  // }

  const onRowCreate = useEventCallback((row: any) => {
    async function onRowCreate(row: RowData) {
      const data = row.data!;
      const dto: CreateDataBlockFieldDto = {
        name: data.name,
        dataType: data.dataType,
        dataBlockTypeId: dataBlockType.id,
        dictionaryId: data.dictionaryId,
        isOptionAllowed: data.isOptionAllowed,
        isBasedOnPrevious: data.isBasedOnPrevious,
        tableColumns: (data as any).tableColumns,
      };
      try {
        await QueryFactory.DataBlockFieldClient.create(dto);
      } catch (error: any) {
        if ('errors' in (error as ValidationProblemDetails)) {
          await modals.showError({ text: parseValidationError(error) });
        } else {
          await modals.showError({ text: parseError(error) });
        }
      }
      await updateFields();
    }

    onRowCreate(row).catch((e) => console.error('onRowCreate', e));
  });

  const onRowDelete = useEventCallback((row: any) => {
    async function onRowDelete(row: RowData) {
      const id = row.key;
      try {
        await QueryFactory.DataBlockFieldClient.remove(id);
      } catch (error) {
        (row as any).cancel = true;
        await modals.showError({ text: parseError(error) });
      }
      await updateFields();
    }
    onRowDelete(row).catch((e) => console.error('onRowDelete', e));
  });
  const [isSaving, setIsSaving] = useState(false);

  const inputFileRef = useRef<HTMLInputElement>(null);
  const [isExcelLoading, setIsExcelLoading] = useState(false);

  async function onImportExcelFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target!.files![0];
    if (!file) {
      return;
    }
    setIsExcelLoading(true);
    try {
      const values = await readFileAsExcel(file);
      const dataGrid = dataGridRef.current!.instance();
      for (let i = 2; i < values.length; i++) {
        const row = values[i];
        try {
          const newRow = await mapExcelRowToDataGrid<DataBlockFieldListDto>(
            dataGrid,
            row,
          );
          if (dataBlockFields.find((x) => x.name === newRow.name)) {
            // do not insert duplicate measurement fields
            continue;
          }
          const initNewRow = (e: any) => {
            Object.assign(e.data, newRow);
          };
          dataGrid.on('initNewRow', initNewRow);
          await dataGrid.addRow();
          dataGrid.off('initNewRow', initNewRow);
        } catch (e) {
          await modals.showError({
            text: `Error parsing excel row ${i}: ${convertToErrorString(e)}`,
          });
          return;
        }
      }
    } finally {
      setIsExcelLoading(false);
      // to allow choosing the same file again
      inputFileRef.current!.value = '';
    }
  }

  const dataGridRef = useRef<DataGrid<DataBlockFieldListDto, number>>(null);

  const onActivationChange = useEventCallback(
    async (row: Row, isDeactivated: boolean) => {
      try {
        const data = row.row.data!;
        setIsSaving(true);
        await QueryFactory.DataBlockFieldClient.patch(data.id, {
          isDeactivated: isDeactivated,
        });
      } catch (error) {
        await modals.showError({ text: parseError(error) });
      } finally {
        setIsSaving(false);
      }
      await updateFields();
    },
  );

  const onRowActivate = useEventCallback((row: Row) => {
    onActivationChange(row, false).catch((e) =>
      console.error('onRowActivate', e),
    );
  });
  const onRowDeactivate = useEventCallback((row: Row) => {
    onActivationChange(row, true).catch((e) =>
      console.error('onRowDeactivate', e),
    );
  });

  const onRename = useEventCallback(async (row: Row) => {
    try {
      setIsSaving(true);
      const data = row.row.data!;
      const newName = await modals.showPrompt({
        defaultValue: data.name,
        title: i18nFields.t('rename_prompt.title'),
        text: i18nFields.t('rename_prompt.text'),
        fieldName: i18nFields.t('rename_prompt.field'),
      });
      if (newName && newName !== data.name) {
        await QueryFactory.DataBlockFieldClient.renameField(data.id, {
          newName: newName,
        });
        await updateFields();
      }
    } catch (error: any) {
      if ('errors' in (error as ValidationProblemDetails)) {
        await modals.showError({ text: parseValidationError(error) });
      } else {
        await modals.showError({ text: parseError(error) });
      }
    } finally {
      setIsSaving(false);
    }
  });
  const editTableHandler = useEventCallback(async (row: Row) => {
    if (row.row.isNewRow) {
      forms.openDataBlockFieldCreation(props.dataBlockType.id, {
        name: row.row.data.name,
        type: row.row.data.dataType,
        tableColumns: (row.row.data as any).tableColumns,
        onSave(data) {
          (row.row.data as any).tableColumns = data.tableColumns;
          row.row.data.name = data.name;
          void dataGridRef.current!.instance().refresh();
        },
      });
    } else {
      forms.openDataBlockFieldEdit(row.row.data.id);
    }
  });
  const dataTypes = useDataTypesList();
  const fieldTypesFunc = useCallback((options: any) => {
    return {
      store: dataTypes,
      // filter: options.isNewRow ? [['value', '<>', DataType.Table]] : null,
    };
  }, []);

  const onExportHandler = useDataGridExportHandler({
    fileName: 'DataBlockFields',
  });

  return (
    <>
      <input
        type="file"
        id="importExcelFile"
        ref={inputFileRef}
        className={'hidden'}
        onChange={onImportExcelFile}
      />
      <Loading
        title={i18n.t('table.excel_loading')}
        loading={isExcelLoading || isSaving}
      >
        <AutoSizedTableWrapper>
          <DXG.DataGrid
            key={props.dataBlockType?.id}
            ref={dataGridRef}
            // onRowUpdating={(props) => onRowUpdate(props.oldData, props.newData)}
            onRowInserting={onRowCreate}
            onRowRemoving={onRowDelete}
            dataSource={dataBlockFields}
            className="dxGridHighlightedOnHover"
            keyExpr="id"
            export={{
              enabled: true,
            }}
            onExporting={onExportHandler}
            onInitNewRow={(e) => {
              const data = e.data as DataBlockFieldListDto;
              data.id = createId() as any;
              data.isBasedOnPrevious = e.data.isBasedOnPrevious ?? false;
            }}
            onToolbarPreparing={(e) => {
              if (!claims.permissions.dataBlockFieldManagement) return;
              e.toolbarOptions!.items!.push({
                location: 'after',
                widget: 'dxButton',
                options: {
                  width: 136,
                  text: i18n.t('table.import_button'),
                  onClick: () => inputFileRef.current!.click(),
                },
              });
              allowOnlySingleNewRow(e);
            }}
            showBorders={true}
            onEditingStart={(e) => {
              if (e.data.dataBlockTypeId === undefined) {
                // new row
                if (e.data.dataType !== DataType.Dictionary) {
                  if (e.column?.dataField === 'dictionaryId') {
                    e.cancel = true;
                  }
                }
              } else {
                //disable editing for all non-new rows
                if (
                  e.column?.dataField === 'isOptionAllowed' ||
                  e.column?.dataField === 'isBasedOnPrevious'
                ) {
                } else {
                  e.cancel = true;
                }
              }
            }}
            onOptionChanged={(props) => {
              if (
                props.name === 'columns' &&
                props.fullName?.includes('sortOrder') &&
                !props.value
              ) {
                props.component?.columnOption('id', 'sortOrder', 'desc');
              }
            }}
            style={{ width: '100%' }}
          >
            <DXG.Paging enabled={false} />
            <DXG.Editing
              mode="batch"
              allowUpdating={claims.permissions.dataBlockFieldManagement}
              allowAdding={claims.permissions.dataBlockFieldManagement}
              allowDeleting={true}
              selectTextOnEditStart={true}
              startEditAction="click"
            />
            <DXG.SearchPanel
              visible={true}
              width={240}
              placeholder={i18n.t('table.search_placeholder')}
            />
            <DXG.Sorting mode="single" />
            <DXG.Column dataField="id" visible={false} />
            <DXG.Column
              sortOrder="asc"
              caption={i18nFields.t('target_name')}
              dataField="name"
              dataType="string"
              calculateDisplayValue={(field: DataBlockFieldListDto) => {
                if (field.isDefault) {
                  const localizationKey = getFieldLocalizationKey(
                    dataBlockType.variant,
                    dataBlockType.value,
                    field.name,
                  );
                  return i18nDictionaries.t(localizationKey, {
                    defaultValue: field.name,
                  });
                }
                return field.name;
              }}
            >
              <DXG.RequiredRule />
            </DXG.Column>
            <DXG.Column
              dataField="dataType"
              caption={i18nFields.t('data_type')}
              dataType="number"
              setCellValue={(e: any, newValue: any, data: any) => {
                e.dataType = newValue;
                e.isOptionAllowed = e.dataType === DataType.Dictionary;
                if (e.dataType !== DataType.Dictionary) {
                  e.dictionaryId = null;
                }
                // to make 'Edit Table' link appear instantely after user have chosen something
                void dataGridRef.current!.instance().refresh();
              }}
            >
              <DXG.Lookup
                dataSource={fieldTypesFunc as any}
                valueExpr="value"
                displayExpr="displayValue"
              />
              <DXG.RequiredRule />
            </DXG.Column>
            <DXG.Column
              dataField={'dictionaryId'}
              caption={i18nFields.t('dictionaryId')}
              dataType="number"
            >
              <DXG.Lookup
                dataSource={sortedDictionaries}
                displayExpr={(item: DataBlockDictionaryDto) =>
                  i18nDictionaries.t(item.title as any)
                }
                valueExpr="id"
              />
              <DXG.CustomRule
                message={i18nFields.t('errors.dictionary_required')}
                validationCallback={(item: any) => {
                  const data = item.data as DataBlockFieldListDto;
                  if (data.dataType === DataType.Dictionary) {
                    return !!data.dictionaryId;
                  }
                  return true;
                }}
              />
            </DXG.Column>
            <DXG.Column
              dataField="isOptionAllowed"
              dataType="boolean"
              caption={i18nFields.t('is_option_allowed')}
              editCellRender={(cell) => {
                if (cell.data.dataType !== DataType.Dictionary) return null;
                return (
                  <DXG.CheckBox
                    value={cell.value}
                    disabled={
                      cell.data.isDefault ||
                      (!claims.permissions.dataBlockFieldManagement &&
                        !claims.permissions.configureMeasurementUI)
                    }
                    onValueChanged={async (e) => {
                      if (!e.event) return;
                      const newValue = e.value;

                      if (cell.row.isNewRow) {
                        dataGridRef
                          .current!.instance()
                          .cellValue(
                            cell.rowIndex,
                            'isOptionAllowed',
                            newValue,
                          );
                        return;
                      }
                      cell.component.beginCustomLoading();

                      try {
                        if (newValue) {
                          await QueryFactory.DataBlockFieldClient.patchLimited(
                            cell.data.id,
                            {
                              isOptionAllowed: true,
                            },
                          ).catch((z) =>
                            console.error('DBFT.isOptionAllowed1', z),
                          );
                        } else {
                          const result = await modals.showMultiButton({
                            title: i18nConvertCustomValueToItem.t('title'),
                            text: i18nConvertCustomValueToItem.t('text'),
                            buttons: [
                              {
                                id: 'yes',
                                text: i18nConvertCustomValueToItem.t(
                                  'yes_button',
                                ),
                                color: ButtonColor.Default,
                              },
                              {
                                id: 'no',
                                text: i18nConvertCustomValueToItem.t(
                                  'no_button',
                                ),
                                color: ButtonColor.Default,
                              },
                              {
                                id: 'cancel',
                                text: i18nConvertCustomValueToItem.t(
                                  'cancel_button',
                                ),
                                color: ButtonColor.Primary,
                              },
                            ],
                          });
                          if (result === 'cancel' || result === null) {
                            return;
                          }

                          await QueryFactory.DataBlockFieldClient.patchLimited(
                            cell.data.id,
                            {
                              isOptionAllowed: false,
                            },
                          );
                          if (result === 'yes') {
                            await QueryFactory.DataBlockFieldClient.createDictionaryItemsFromCustomValues(
                              cell.data.id,
                            );
                          }
                        }

                        await queryClient.invalidateQueries({
                          queryKey:
                            QueryFactory.DataBlockTypeQuery.getUIConfigsQueryKey(
                              undefined!,
                            ),
                        });
                        await fieldQuery.refetch();
                      } catch (err: any) {
                        void modals.showError({ text: err.message });
                      } finally {
                        cell.component.endCustomLoading();
                      }
                      cell.data.isOptionAllowed = newValue;
                      cell.component.refresh();
                    }}
                  />
                );
              }}
            ></DXG.Column>
            <DXG.Column
              dataField="isBasedOnPrevious"
              dataType="boolean"
              caption={i18nFields.t('is_based_on_previous')}
              editCellRender={(cell) => {
                if (
                  dataBlockType.variant === DataBlockVariant.SharedFields &&
                  dataBlockType.value !== sharedTypeNames.case &&
                  dataBlockType.value !== sharedTypeNames.visit
                )
                  return null;
                return (
                  <DXG.CheckBox
                    disabled={
                      !claims.permissions.dataBlockFieldManagement &&
                      !claims.permissions.configureMeasurementUI
                    }
                    value={cell.value}
                    onValueChanged={async (e) => {
                      if (!e.event) return;
                      const newValue = e.value;

                      if (cell.row.isNewRow) {
                        dataGridRef
                          .current!.instance()
                          .cellValue(
                            cell.rowIndex,
                            'isBasedOnPrevious',
                            newValue,
                          );
                        return;
                      }
                      cell.component.beginCustomLoading();

                      try {
                        await QueryFactory.DataBlockFieldClient.patchLimited(
                          cell.data.id,
                          {
                            isBasedOnPrevious: newValue,
                          },
                        ).catch((z) =>
                          console.error('DBFT.isBasedOnPrevious1', z),
                        );

                        await queryClient.invalidateQueries({
                          queryKey:
                            QueryFactory.DataBlockTypeQuery.getUIConfigsQueryKey(
                              undefined!,
                            ),
                        });
                      } catch (err: any) {
                        void modals.showError({ text: err.message });
                      } finally {
                        cell.component.endCustomLoading();
                      }
                      cell.data.isBasedOnPrevious = newValue;
                      cell.component.refresh();
                    }}
                  />
                );
              }}
            ></DXG.Column>
            <DXG.Column
              type="buttons"
              visible={claims.permissions.dataBlockFieldManagement}
            >
              <DXG.Button name="delete" visible={isDeleteVisible as any} />
              <DXG.Button
                text={i18nFields.t('activate_button')}
                visible={isActivateVisible as any}
                onClick={onRowActivate as any}
              />
              <DXG.Button
                text={i18nFields.t('deactivate_button')}
                visible={isDeactivateVisible as any}
                onClick={onRowDeactivate as any}
              />
              <DXG.Button
                text={i18nFields.t('rename_button')}
                visible={isRenameVisible as any}
                onClick={onRename as any}
              />
              <DXG.Button
                text={i18nFields.t('edit_table')}
                visible={isEditTableVisible as any}
                onClick={editTableHandler as any}
              />
            </DXG.Column>
          </DXG.DataGrid>
        </AutoSizedTableWrapper>
      </Loading>
    </>
  );
};

function isDeleteVisible(row: Row) {
  const data = row.row.data;
  return !data.isDefault && !row.row.removed;
}

function isActivateVisible(row: Row) {
  const data = row.row.data;

  return isDeleteVisible(row) && data.isDeactivated;
}

function isDeactivateVisible(row: Row) {
  const data = row.row.data;

  return isDeleteVisible(row) && !data.isDeactivated && !row.row.isNewRow;
}

function isRenameVisible(row: Row) {
  const data = row.row.data;

  return !data.isDefault && data.dataBlockTypeId;
}
function isEditTableVisible(row: Row) {
  const data = row.row.data;

  return !data.isDefault && data.dataType === DataType.Table;
}
